// A Stats tile showing a team (/team-stats; teams and matches, Ben 2026-10-30). A team is compared
// with the other teams in its league that season (rules.md, Row 2), so one answer per category
// holds every team: the team's dot, and the track the league's teams make.

import type { SeasonsResponse, TeamStatsResponse, TeamStatsRow } from "@catbento/shared";
import { liveApi, type LiveApi } from "./api.js";

export async function loadTeamStats(api: LiveApi, competitionId: number, seasonCode: string, category: string): Promise<TeamStatsResponse> {
  return api.get<TeamStatsResponse>(`/team-stats?competitionId=${competitionId}&seasonId=${seasonCode}&category=${encodeURIComponent(category)}`);
}

/** One league season's answer, under the key every team tile shares. */
export function teamStatsQuery(competitionId: number, seasonCode: string, category: string): { key: string; load: () => Promise<TeamStatsResponse> } {
  return { key: `team-stats:${competitionId}:${seasonCode}:${category}`, load: () => loadTeamStats(liveApi(), competitionId, seasonCode, category) };
}

/** A league's seasons, newest first (the season ids, "52026"), under the key the team maps share. */
export function leagueSeasonsQuery(competitionId: number): { key: string; load: () => Promise<string[]> } {
  return {
    key: `team-shots:seasons:${competitionId}`,
    load: async () => (await liveApi().get<SeasonsResponse>(`/seasons?competitionId=${competitionId}`)).seasons.map((s) => String(s.id)),
  };
}

/** A team's own stats where less is better (cards, failed dribbles, own goals). */
const TEAM_LOWER_IS_BETTER: ReadonlySet<string> = new Set([
  "yellow_cards_90",
  "second_yellow_cards_90",
  "red_cards_90",
  "failed_dribbles_90",
  "own_goals_90",
]);

/** Is a lower value better? An opponent's stat turns round: fewer shots conceded is good, but more
 *  cards for the other side is too. Pure. */
export function teamLowerIsBetter(key: string): boolean {
  if (key.startsWith("opponent_")) return !TEAM_LOWER_IS_BETTER.has(key.slice("opponent_".length));
  return TEAM_LOWER_IS_BETTER.has(key);
}

export interface TeamStatBar {
  /** The team's value, or null when it has none. */
  value: number | null;
  /** Every team's value that has one, lowest first. */
  values: number[];
  /** The track: the league's lowest to highest (20 teams have no tiny-minute outliers to trim,
   *  unlike a player track's 5th–95th percentile). Null with fewer than two teams. */
  track: { min: number; max: number } | null;
}

/** One stat's bar for one team among its league's teams. Pure. */
export function teamStatBar(rows: readonly TeamStatsRow[], teamId: number, key: string): TeamStatBar {
  const values = rows
    .map((r) => r.values[key])
    .filter((v): v is number => v !== null && v !== undefined && !Number.isNaN(v))
    .sort((a, b) => a - b);
  const own = rows.find((r) => r.teamId === teamId)?.values[key];
  const value = own === null || own === undefined || Number.isNaN(own) ? null : own;
  const track = values.length >= 2 ? { min: values[0]!, max: values[values.length - 1]! } : null;
  return { value, values, track };
}

/** A stat's track on a tile comparing teams: the lowest to the highest of every team in the leagues
 *  on it (two teams from one league share their answer; it counts once). Null with fewer than two
 *  values. Pure. */
export function teamComparisonTrack(answers: readonly (TeamStatsResponse | null)[], key: string): { min: number; max: number } | null {
  const rows = [...new Set(answers.filter((a): a is TeamStatsResponse => a !== null))].flatMap((a) => a.rows);
  return teamStatBar(rows, -1, key).track;
}
